/* global __BUILD_VERSION__, __BUILD_TIMESTAMP__ */
/**
 * Deployment Manager - Handles version tracking and deployment-related recovery
 *
 * This utility helps detect when the application has been updated while the user
 * still has an old version running, which can cause chunk loading errors.
 */

/**
 * Gets the current build version from compile-time constants
 * @returns {string} - Current build version
 */
export const getBuildVersion = () => {
    try {
        return typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'development'
    } catch {
        return 'development'
    }
}

/**
 * Gets the current build timestamp from compile-time constants
 * @returns {string} - Current build timestamp
 */
export const getBuildTimestamp = () => {
    try {
        return typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : new Date().toISOString()
    } catch {
        return new Date().toISOString()
    }
}

/**
 * Stores the initial build version when the app loads
 */
let initialBuildVersion = null

/**
 * Initializes the deployment manager by storing the current build version
 * Should be called early in the application lifecycle
 */
export const initializeDeploymentManager = () => {
    initialBuildVersion = getBuildVersion()

    // Store version in session storage for persistence across navigation
    try {
        sessionStorage.setItem('app_build_version', initialBuildVersion)
        sessionStorage.setItem('app_build_timestamp', getBuildTimestamp())

        // Add to window for global access
        window.APP_BUILD_VERSION = initialBuildVersion
        window.APP_BUILD_TIMESTAMP = getBuildTimestamp()
    } catch (error) {
        console.warn('Could not store build version in session storage:', error)
    }

    console.info(`Application initialized with build version: ${initialBuildVersion}`)
}

/**
 * Checks if the current version matches the initially loaded version
 * @returns {boolean} - True if versions match, false if there's a mismatch
 */
export const isVersionConsistent = () => {
    const currentVersion = getBuildVersion()
    const storedVersion = initialBuildVersion || sessionStorage.getItem('app_build_version')

    return currentVersion === storedVersion
}

/**
 * Gets information about the current deployment state
 * @returns {Object} - Deployment info object
 */
export const getDeploymentInfo = () => {
    return {
        currentVersion: getBuildVersion(),
        currentTimestamp: getBuildTimestamp(),
        initialVersion: initialBuildVersion,
        storedVersion: sessionStorage.getItem('app_build_version'),
        storedTimestamp: sessionStorage.getItem('app_build_timestamp'),
        isConsistent: isVersionConsistent(),
        userAgent: navigator.userAgent,
        url: window.location.href,
    }
}

/**
 * Handles deployment mismatch by reloading the application with cache clearing
 * @param {string} reason - Reason for the deployment recovery
 * @param {Object} additionalInfo - Additional context information
 */
export const handleDeploymentMismatch = (reason = 'Version mismatch detected', additionalInfo = {}) => {
    const deploymentInfo = getDeploymentInfo()

    console.warn('Deployment mismatch detected, triggering recovery:', {
        reason,
        ...deploymentInfo,
        ...additionalInfo,
    })

    // Track deployment recovery
    if (window.appInsights) {
        window.appInsights.trackEvent({
            name: 'DeploymentMismatchRecovery',
            properties: {
                reason,
                ...deploymentInfo,
                ...additionalInfo,
            },
        })
    }

    // Clear session storage to start fresh
    try {
        sessionStorage.removeItem('app_build_version')
        sessionStorage.removeItem('app_build_timestamp')
    } catch (error) {
        console.warn('Could not clear session storage:', error)
    }

    // Clear caches and reload
    clearCachesAndReload()
}

/**
 * Clears browser caches and reloads the page
 * Used for deployment recovery scenarios
 */
export const clearCachesAndReload = () => {
    // Clear service worker caches if available
    if ('caches' in window) {
        try {
            caches
                .keys()
                .then((names) => {
                    const clearPromises = names.map((name) => {
                        if (
                            name.includes('vite') ||
                            name.includes('workbox') ||
                            name.includes('runtime') ||
                            name.includes('precache')
                        ) {
                            return caches.delete(name)
                        }
                        return Promise.resolve()
                    })

                    return Promise.all(clearPromises)
                })
                .then(() => {
                    console.info('Caches cleared, reloading application')
                    window.location.reload()
                })
                .catch((error) => {
                    console.warn('Cache clearing failed, performing simple reload:', error)
                    window.location.reload()
                })
        } catch (error) {
            console.warn('Cache API not available, performing simple reload:', error)
            window.location.reload()
        }
    } else {
        console.info('Cache API not available, performing simple reload')
        window.location.reload()
    }
}

/**
 * Checks for deployment updates by comparing versions
 * Can be used for periodic checks or manual refresh detection
 * @returns {Promise<boolean>} - True if update detected, false otherwise
 */
export const checkForDeploymentUpdate = async () => {
    try {
        // In a real implementation, this could fetch version info from the server
        // For now, we'll compare with the stored initial version
        const deploymentInfo = getDeploymentInfo()

        if (!deploymentInfo.isConsistent) {
            console.warn('Deployment update detected:', deploymentInfo)
            return true
        }

        return false
    } catch (error) {
        console.warn('Error checking for deployment update:', error)
        return false
    }
}

// Auto-initialize when module is loaded
initializeDeploymentManager()
